import type {
  DestinationNode,
  OrderDraft,
  OrderEditField,
  OrderService,
  OrderablePackage,
  ShippingChannel,
  SubmittedOrder,
} from '@mypoz/shared';
import { findDestination, orderToDraft } from '@mypoz/shared';
import { useTranslation } from '@mypoz/i18n';
import { Card, EmptyState } from '@mypoz/ui';

export interface ChangeReviewProps {
  order: SubmittedOrder;
  draft: OrderDraft;
  changed: readonly OrderEditField[];
  /** True when the edit invalidates a fee breakdown the order already carries. */
  feesStale: boolean;
  /**
   * Whether saving sends the edit to the warehouse (`order_modify`) or only changes this browser's
   * copy — the case for an order built in this session, which the warehouse has never seen.
   */
  sendsToWarehouse: boolean;
  pool: readonly OrderablePackage[];
  channels: readonly ShippingChannel[];
  services: readonly OrderService[];
  countries: readonly DestinationNode[];
  provincesFor: (countryCode: string) => readonly DestinationNode[];
  citiesFor: (provinceCode: string) => readonly DestinationNode[];
  /** Jumps back to the step that owns a field. */
  onEdit: (stepIndex: number) => void;
}

/** Which step owns each field, so a row can send you back to it. */
const STEP_OF: Record<OrderEditField, number> = {
  packages: 0,
  channel: 1,
  destination: 2,
  recipient: 2,
  phone: 2,
  line1: 2,
  postcode: 2,
  services: 2,
  paymentMode: 2,
  couponCode: 2,
  remark: 2,
};

/**
 * The last step in edit mode: what you changed, before and after.
 *
 * Not a second copy of the whole order. Confirming a create means checking an order you have never
 * seen; confirming an edit means checking the handful of things you touched, and burying those in
 * a full summary is how a wrong coupon code gets saved. Everything untouched is already correct by
 * definition — it is what was there before.
 */
export function ChangeReview({
  order,
  draft,
  changed,
  feesStale,
  sendsToWarehouse,
  pool,
  channels,
  services,
  countries,
  provincesFor,
  citiesFor,
  onEdit,
}: ChangeReviewProps) {
  const { t } = useTranslation();

  const before = orderToDraft(order);

  const packageNames = (ids: readonly string[]) =>
    pool
      .filter((item) => ids.includes(item.id))
      .map((item) => item.trackingNumber)
      .join(', ') || t('orders.edit.review.none');

  const channelName = (id: string | undefined) =>
    channels.find((item) => item.id === id)?.name ?? t('orders.edit.review.none');

  const destinationName = (source: OrderDraft) => {
    const country = findDestination(countries, source.countryCode);
    const province = findDestination(
      source.countryCode ? provincesFor(source.countryCode) : [],
      source.provinceCode,
    );
    const city = findDestination(
      source.provinceCode ? citiesFor(source.provinceCode) : [],
      source.cityCode,
    );
    return (
      [city?.name, province?.name, country?.name].filter(Boolean).join(', ') ||
      t('orders.edit.review.none')
    );
  };

  const serviceNames = (ids: readonly string[]) =>
    services
      .filter((item) => ids.includes(item.id))
      .map((item) => item.name)
      .join(', ') || t('orders.edit.review.none');

  const text = (value: string) => value.trim() || t('orders.edit.review.none');

  const render = (source: OrderDraft, field: OrderEditField): string => {
    switch (field) {
      case 'packages':
        return packageNames(source.packageIds);
      case 'channel':
        return channelName(source.channelId);
      case 'destination':
        return destinationName(source);
      case 'recipient':
        return text(source.recipient);
      case 'phone':
        return text(source.phone);
      case 'line1':
        return text(source.line1);
      case 'postcode':
        return text(source.postcode);
      case 'services':
        return serviceNames(source.serviceIds);
      case 'paymentMode':
        return source.paymentMode === 'MANUAL'
          ? t('orders.newOrder.delivery.paymentManual')
          : t('orders.newOrder.delivery.paymentAuto');
      case 'couponCode':
        return text(source.couponCode);
      case 'remark':
        return text(source.remark);
    }
  };

  if (changed.length === 0) {
    return (
      <EmptyState
        icon="edit_note"
        title={t('orders.edit.review.empty')}
        description={t('orders.edit.review.emptyBody')}
      />
    );
  }

  return (
    <div className="grid gap-5">
      <Card className="p-0">
        <div className="border-b border-line px-5 py-4 sm:px-6">
          {/* `count` here is doing pluralisation, which is the one job i18next reserves it for. */}
          <h2 className="text-base font-semibold text-ink">
            {t('orders.edit.review.title', { count: changed.length })}
          </h2>
        </div>

        <div className="divide-y divide-line">
          {changed.map((field) => (
            <div
              key={field}
              className="grid gap-2 px-5 py-4 sm:grid-cols-[10rem_1fr_auto] sm:items-baseline sm:gap-4 sm:px-6"
            >
              <h3 className="text-sm font-semibold text-ink">{t(`orders.edit.field.${field}`)}</h3>

              <div className="min-w-0 text-sm">
                {/*
                  The old value keeps a strikethrough as well as muted colour. Colour alone would
                  make before and after tell apart only by shade, which is exactly the distinction
                  that disappears for a colour-blind reader or on a dim phone screen.
                */}
                <p className="text-muted line-through decoration-muted-soft">
                  {render(before, field)}
                </p>
                <p className="mt-0.5 font-medium break-words text-ink">{render(draft, field)}</p>
              </div>

              <button
                type="button"
                onClick={() => onEdit(STEP_OF[field])}
                className="min-h-11 justify-self-start text-sm font-semibold text-brand transition-colors hover:text-brand-dark focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand sm:justify-self-end lg:min-h-0"
              >
                {t('orders.newOrder.confirm.edit')}
              </button>
            </div>
          ))}
        </div>
      </Card>

      {/*
        Staleness, not a recalculation. The fee schedule is MYP-22 and on hold, so the honest thing
        is to say the figures on screen no longer describe this order — a total invented here would
        be the most convincing wrong number in the app.
      */}
      {feesStale ? (
        <p className="border-l-2 border-warning pl-3 text-sm text-ink">
          {t('orders.edit.review.feesStale')}
        </p>
      ) : null}

      <p className="border-l-2 border-brand pl-3 text-sm text-ink">
        {sendsToWarehouse ? t('orders.edit.review.sent') : t('orders.edit.review.notSent')}
      </p>
    </div>
  );
}
